import { InputField } from '@/components/form/InputField';
import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Loadable } from '@/components/Loadable';
import { Button } from '@/components/primitives/Button';
import { CardContent, CardPrimitiveRoot } from '@/components/primitives/Card';
import { Form } from '@/components/primitives/Form';
import { Typography } from '@/components/primitives/Typography';
import { manageBookingSearchLoadingSelector } from '@/features/loading/loadingSelectors';
import { useBookingSearch } from '@/hooks/useBookingSearch';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import {
  createFindBookingSchema,
  type FindBookingValues,
} from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';

const BookingSearch = () => {
  const loading = useSelector(manageBookingSearchLoadingSelector);
  const { t } = useTranslation();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  usePageTitle('manageBooking');

  const form = useForm<FindBookingValues>({
    mode: 'onBlur',
    resolver: zodResolver(createFindBookingSchema(t)),
    defaultValues: {
      email: '',
      bookingNumber: '',
    },
  });

  const onSubmit = useBookingSearch();

  return (
    <CenteredLayout>
      <Loadable loading={loading} overlay>
        <div className="mx-auto flex w-full max-w-[410px] flex-col gap-8 p-4 tablet:my-auto laptop:p-0">
          <Typography
            variant={isLaptopOrBigger ? 'title' : 'mobile-title'}
            className="text-left"
            asChild
          >
            <h1>
              <TransText i18nKey="manageBooking" />
            </h1>
          </Typography>

          <CardPrimitiveRoot className="rounded-xl">
            <CardContent>
              <Form
                form={form}
                onSubmit={form.handleSubmit(onSubmit)}
                className={cn('flex flex-col gap-3 [&_input]:h-12', {
                  '[&_input]:h-8': isLaptopOrBigger,
                })}
              >
                <InputField
                  name="email"
                  required
                  placeholder={t('text.enterYourEmailAddress')}
                  label={<TransText i18nKey="emailUsedForBooking" />}
                />
                <InputField
                  name="bookingNumber"
                  required
                  placeholder={t('text.enterYourBookingNumber')}
                  label={<TransText i18nKey="bookingNumber" />}
                />
                <Button
                  size="medium"
                  className="mt-3"
                  data-testid="find-booking-btn"
                  type="submit"
                  fullWidth
                >
                  <TransText i18nKey="findYourBooking" />
                </Button>
              </Form>
            </CardContent>
          </CardPrimitiveRoot>
        </div>
      </Loadable>
    </CenteredLayout>
  );
};

export default BookingSearch;
